import { useState } from 'react';
import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FLOW_GROUPS, type LatestFlowByMarket } from '../flow/groups';
import { quoteWith } from '../model/capabilities';
import type { Outcome } from '../model/types';
import { OutcomeListPlaceholder } from './OutcomeListPlaceholder';
import { FLOW_SLOT_WIDTH, OUTCOME_GRID, OutcomeRow } from './OutcomeRow';
import { PhoneOutcomeList } from './phone/PhoneOutcomeList';
import { useOutcomesOnPhone } from './phone/useOutcomesOnPhone';
import type { OutcomeSort } from './ranking';

export interface OutcomeListProps {
    shown: readonly Outcome[];
    hidden: readonly Outcome[];
    selectedIds: readonly string[];
    colors: ReadonlyMap<string, string>;
    totalVolume: number;
    clickable: boolean;
    showFlow: boolean;
    latestFlow: LatestFlowByMarket;
    sort: OutcomeSort;
    onSortChange: (sort: OutcomeSort) => void;
    onToggle: (id: string) => void;
    onSelectAll?: () => void;
    onClear: () => void;
    /** Placeholders in place of the rows, while the list is not complete. */
    loading?: boolean;
}

function HeadCell({
    children,
    align = 'right',
}: {
    children: React.ReactNode;
    align?: 'left' | 'right';
}) {
    return (
        <Typography
            sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                textAlign: align,
            }}
        >
            {children}
        </Typography>
    );
}

/** Cards below 600 px, the table from there up. */
export function OutcomeList(props: OutcomeListProps) {
    const phone = useOutcomesOnPhone();
    if (props.loading) return <OutcomeListPlaceholder phone={phone} />;
    return phone ? (
        <PhoneOutcomeList {...props} />
    ) : (
        <OutcomeTable {...props} />
    );
}

function OutcomeTable({
    shown,
    hidden,
    selectedIds,
    colors,
    totalVolume,
    clickable,
    showFlow,
    latestFlow,
    sort,
    onSortChange,
    onToggle,
    onSelectAll,
    onClear,
}: OutcomeListProps) {
    const [showHidden, setShowHidden] = useState(false);
    const selected = new Set(selectedIds);
    const total = shown.length + hidden.length;
    const canSelectAll =
        clickable &&
        onSelectAll !== undefined &&
        total > 1 &&
        selectedIds.length < total;

    const row = (outcome: Outcome) => {
        const marketId = quoteWith(outcome, 'flow')?.market.marketId;
        return (
            <OutcomeRow
                key={outcome.id}
                outcome={outcome}
                color={colors.get(outcome.id) ?? null}
                selected={selected.has(outcome.id)}
                clickable={clickable}
                totalVolume={totalVolume}
                showFlow={showFlow}
                flow={marketId ? latestFlow.get(marketId) : undefined}
                onToggle={() => onToggle(outcome.id)}
            />
        );
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                }}
            >
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                    Outcomes
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {total}
                </Typography>
                {canSelectAll && (
                    <Button
                        size="small"
                        onClick={onSelectAll}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Show all {total} on the chart
                    </Button>
                )}
                {clickable && selectedIds.length > 0 && (
                    <Button
                        size="small"
                        onClick={onClear}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Clear selection
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={sort}
                    onChange={(_, value: OutcomeSort | null) => {
                        if (value) onSortChange(value);
                    }}
                    sx={{
                        height: 24,
                        '& .MuiToggleButton-root': {
                            py: 0,
                            px: 1,
                            fontSize: '11px',
                            textTransform: 'none',
                        },
                    }}
                >
                    <ToggleButton value="probability">Probability</ToggleButton>
                    <ToggleButton value="volume">Volume</ToggleButton>
                    {showFlow && <ToggleButton value="flow">Flow</ToggleButton>}
                </ToggleButtonGroup>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: showFlow
                        ? OUTCOME_GRID.withFlow
                        : OUTCOME_GRID.withoutFlow,
                    columnGap: 2,
                    px: 1.5,
                    py: 0.5,
                }}
            >
                <HeadCell align="left">Outcome</HeadCell>
                <HeadCell>Probability</HeadCell>
                {showFlow && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${FLOW_GROUPS.length}, ${FLOW_SLOT_WIDTH}px)`,
                            columnGap: '6px',
                        }}
                    >
                        {FLOW_GROUPS.map(group => (
                            <Box
                                key={group.id}
                                title={`${group.label}: net capital and number of wallets`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: group.color,
                                    }}
                                />
                                <HeadCell align="left">{group.label}</HeadCell>
                            </Box>
                        ))}
                    </Box>
                )}
                <HeadCell>Volume</HeadCell>
            </Box>

            {shown.map(row)}

            {hidden.length > 0 && (
                <>
                    <Button
                        size="small"
                        onClick={() => setShowHidden(v => !v)}
                        sx={{
                            fontSize: '11px',
                            textTransform: 'none',
                            ml: 0.5,
                            mt: 0.5,
                        }}
                    >
                        {showHidden
                            ? 'Hide the other outcomes'
                            : `Show ${hidden.length} more outcome${hidden.length === 1 ? '' : 's'}`}
                    </Button>
                    {showHidden && hidden.map(row)}
                </>
            )}
        </Box>
    );
}
